import {
  ExposedDocumentation,
  ExposedDocumentationModel,
} from '@contexts/Estate/EstateApplication/domain/models';
import {
  IdentityValidation,
  IdentityValidationModel,
} from '@contexts/IdentityValidation/domain';
import {
  EstateApplication,
  EstateApplicationModel,
} from './exposed-estate-application.model';
import { Dni, UserId } from './types';
import { UserInformation } from './user-information.model';
import { ApplicationStateSteps } from '@contexts/Estate/EstateApplication/useCases';

type Args = {
  loggedInUserId: UserId;
  estateApplication: EstateApplication;
  identityValidation: IdentityValidation;
  creditStudyDocumentation: ExposedDocumentation;
  userInformation?: UserInformation;
};

export class UserEstateApplication {
  constructor(
    public readonly userId: UserId,
    public readonly estateApplication: EstateApplication,
    public readonly identityValidation: IdentityValidation,
    public readonly creditStudyDocumentation: ExposedDocumentation,
    public readonly userInformation?: UserInformation
  ) {}

  static of = ({
    loggedInUserId,
    estateApplication,
    identityValidation,
    creditStudyDocumentation,
    userInformation,
  }: Args): UserEstateApplication =>
    new UserEstateApplication(
      loggedInUserId,
      estateApplication,
      identityValidation,
      creditStudyDocumentation,
      userInformation
    );

  mapToStateStep(isCotenant: boolean): ApplicationStateSteps {
    const isIdentityValidationApproved = IdentityValidationModel.isApproved(
      this.identityValidation
    );
    const isApplicationExpired = EstateApplicationModel.isExpired(
      this.estateApplication
    );

    if (isCotenant && isApplicationExpired)
      return ApplicationStateSteps.leaveApplication;

    const allowsDocumentUpload = ExposedDocumentationModel.canEditDocuments(
      this.creditStudyDocumentation
    );
    const isDocumentationCompleted = ExposedDocumentationModel.isUserDone(
      this.creditStudyDocumentation
    );
    const isCotenantAlreadyAdded =
      isCotenant &&
      EstateApplicationModel.isCotenantAlreadyAdded(
        this.estateApplication,
        this.userId
      );

    const userSuccesfullyApplied =
      (!isCotenant || isCotenantAlreadyAdded) &&
      !isApplicationExpired &&
      isDocumentationCompleted &&
      !!this.userInformation &&
      isIdentityValidationApproved;

    if (userSuccesfullyApplied)
      return ApplicationStateSteps.successfulApplication;

    const mustUploadDocuments =
      (!isCotenant || isCotenantAlreadyAdded) &&
      !isApplicationExpired &&
      allowsDocumentUpload &&
      !!this.userInformation &&
      isIdentityValidationApproved;

    if (mustUploadDocuments) return ApplicationStateSteps.documentsUpload;

    const mustProvideFinancialInfo =
      !isApplicationExpired &&
      !this.userInformation &&
      isIdentityValidationApproved;

    if (mustProvideFinancialInfo)
      return ApplicationStateSteps.applicantCharacterization;

    const isCotenantDocumentCheck =
      isCotenant &&
      isCotenantAlreadyAdded &&
      !this.userInformation &&
      !isIdentityValidationApproved;

    if (isCotenantDocumentCheck) return ApplicationStateSteps.documentsCheck;

    const isTenantOnboarding =
      !isCotenant &&
      !isApplicationExpired &&
      !this.userInformation &&
      !isIdentityValidationApproved;

    if (isTenantOnboarding) return ApplicationStateSteps.onboarding;

    const cotenantOnboarding =
      isCotenant &&
      !isApplicationExpired &&
      EstateApplicationModel.canAddCotenant(this.estateApplication) &&
      !isCotenantAlreadyAdded;

    if (cotenantOnboarding) return ApplicationStateSteps.addCotenant;

    return ApplicationStateSteps.errorApplication;
  }
}

export class BasicUserData {
  constructor(
    public readonly firstname: string,
    public readonly lastname: string,
    public readonly dni?: Dni
  ) {}
}
